import { useState, useEffect } from 'react';
import { ensureCsrfToken, fetchCsrfToken, peekCsrfToken } from '../services/csrf';

/**
 * Hook to manage CSRF tokens for API requests.
 *
 * Token attachment itself lives in `services/api.ts`, on the `api` axios
 * instance — that is the only place it can work, since every app request goes
 * through that instance. This hook's job is to warm the token on page load and
 * refresh it before the backend's 1-hour TTL expires, so that requests are not
 * paying for a token fetch on the critical path.
 */
export const useCsrfToken = () => {
  const [csrfToken, setCsrfToken] = useState<string | null>(peekCsrfToken());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async (force: boolean) => {
    setLoading(true);
    setError(null);

    // On mount we reuse an existing token: a page navigation should not mint a
    // new one, both to avoid churning Redis and because the token is expected
    // to stay stable for the life of the session.
    const token = force ? await fetchCsrfToken() : await ensureCsrfToken();
    setCsrfToken(token);
    if (!token) {
      setError('Failed to fetch CSRF token');
    }

    setLoading(false);
    return token;
  };

  /** Force a brand-new token from the backend. */
  const refreshToken = () => load(true);

  useEffect(() => {
    // Reuse the stored token if this session already has one.
    load(false);

    // Refresh before expiry (50 minutes for the backend's 1 hour TTL)
    const refreshInterval = 50 * 60 * 1000;
    const intervalId = setInterval(() => {
      refreshToken();
    }, refreshInterval);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  return {
    csrfToken,
    loading,
    error,
    refreshToken,
  };
};

export { getCsrfToken, addCsrfToHeaders } from '../services/csrf';
